import { call, put, takeEvery, takeLatest, all, select } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { workflowApi, CreateWorkflowRequest, UpdateWorkflowRequest, CreateApprovalLevelRequest, UpdateApprovalLevelRequest } from '../../services/workflowApi';
import {
  setLoading,
  setBulkLoading,
  setError,
  setWorkflows,
  setSelectedWorkflow,
  setRoles,
  setUsers,
  setStatuses,
  setEventTypes,
  setGroups,
  addWorkflow,
  updateWorkflow,
  removeWorkflow,
  addApprovalLevel,
  updateApprovalLevel,
  removeApprovalLevel,
  setDialogOpen,
  setApprovalLevelDialogOpen,
  setValidationErrors,
  setApprovalLevelValidationErrors,
  setStatistics,
  deselectAllWorkflows,
  clearNewApprovalLevels,
  FETCH_WORKFLOWS,
  FETCH_WORKFLOW_BY_ID,
  CREATE_WORKFLOW,
  UPDATE_WORKFLOW,
  DELETE_WORKFLOW,
  FETCH_ROLES,
  FETCH_USERS,
  FETCH_STATUSES,
  FETCH_EVENT_TYPES,
  FETCH_GROUPS,
  CREATE_APPROVAL_LEVEL,
  UPDATE_APPROVAL_LEVEL,
  DELETE_APPROVAL_LEVEL,
  BULK_ACTIVATE_WORKFLOWS,
  BULK_DEACTIVATE_WORKFLOWS,
  FETCH_WORKFLOW_STATISTICS,
} from '../reducers/workflowSlice';
import { RootState } from '../index';

// Worker Sagas
function* fetchWorkflowsSaga(): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const workflows = yield call(workflowApi.getAll);
    yield put(setWorkflows(workflows));
  } catch (error: any) {
    console.error('Error fetching workflows:', error);
    yield put(setError(error.message || 'Failed to fetch workflows'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchWorkflowByIdSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const workflow = yield call(workflowApi.getById, action.payload);
    yield put(setSelectedWorkflow(workflow));
  } catch (error: any) {
    console.error('Error fetching workflow:', error);
    yield put(setError(error.message || 'Failed to fetch workflow'));
  } finally {
    yield put(setLoading(false));
  }
}

function* createWorkflowSaga(action: PayloadAction<CreateWorkflowRequest>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    // Validate the workflow
    const validationErrors = yield call(workflowApi.validation.validateBeforeSave, action.payload);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const newWorkflow = yield call(workflowApi.create, action.payload);
    yield put(addWorkflow(newWorkflow));
    yield put(setDialogOpen(false));
    yield put(setValidationErrors([]));
    yield put(clearNewApprovalLevels());
    
    // Refresh statistics
    yield call(fetchWorkflowStatisticsSaga);
  } catch (error: any) {
    console.error('Error creating workflow:', error);
    yield put(setError(error.message || 'Failed to create workflow'));
  } finally {
    yield put(setLoading(false));
  }
}

function* updateWorkflowSaga(action: PayloadAction<UpdateWorkflowRequest>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    // Validate the workflow
    const validationErrors = yield call(workflowApi.validation.validateBeforeSave, action.payload);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const updatedWorkflow = yield call(workflowApi.update, action.payload);
    yield put(updateWorkflow(updatedWorkflow));
    yield put(setDialogOpen(false));
    yield put(setValidationErrors([]));
    yield put(clearNewApprovalLevels());
    
    // Refresh statistics
    yield call(fetchWorkflowStatisticsSaga);
  } catch (error: any) {
    console.error('Error updating workflow:', error);
    yield put(setError(error.message || 'Failed to update workflow'));
  } finally {
    yield put(setLoading(false));
  }
}

function* deleteWorkflowSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    yield call(workflowApi.delete, action.payload);
    yield put(removeWorkflow(action.payload));
    
    // Refresh statistics
    yield call(fetchWorkflowStatisticsSaga);
  } catch (error: any) {
    console.error('Error deleting workflow:', error);
    yield put(setError(error.message || 'Failed to delete workflow'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchRolesSaga(): Generator<any, void, any> {
  try {
    const roles = yield call(workflowApi.getRoles);
    yield put(setRoles(roles));
  } catch (error: any) {
    console.error('Error fetching roles:', error);
    yield put(setError(error.message || 'Failed to fetch roles'));
  }
}

function* fetchUsersSaga(): Generator<any, void, any> {
  try {
    const users = yield call(workflowApi.getUsers);
    yield put(setUsers(users));
  } catch (error: any) {
    console.error('Error fetching users:', error);
    yield put(setError(error.message || 'Failed to fetch users'));
  }
}

function* fetchStatusesSaga(): Generator<any, void, any> {
  try {
    const statuses = yield call(workflowApi.getStatuses);
    yield put(setStatuses(statuses));
  } catch (error: any) {
    console.error('Error fetching statuses:', error);
    yield put(setError(error.message || 'Failed to fetch statuses'));
  }
}

function* fetchEventTypesSaga(): Generator<any, void, any> {
  try {
    const eventTypes = yield call(workflowApi.getEventTypes);
    yield put(setEventTypes(eventTypes));
  } catch (error: any) {
    console.error('Error fetching event types:', error);
    yield put(setError(error.message || 'Failed to fetch event types'));
  }
}

function* fetchGroupsSaga(): Generator<any, void, any> {
  try {
    const groups = yield call(workflowApi.getGroups);
    yield put(setGroups(groups));
  } catch (error: any) {
    console.error('Error fetching groups:', error);
    yield put(setError(error.message || 'Failed to fetch groups'));
  }
}

function* createApprovalLevelSaga(action: PayloadAction<CreateApprovalLevelRequest>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    // Validate the approval level
    const validationErrors = yield call(workflowApi.validation.validateApprovalLevel, action.payload);
    if (validationErrors.length > 0) {
      yield put(setApprovalLevelValidationErrors(validationErrors));
      return;
    }
    
    const newApprovalLevel = yield call(workflowApi.createApprovalLevel, action.payload);
    yield put(addApprovalLevel(newApprovalLevel));
    yield put(setApprovalLevelDialogOpen(false));
    yield put(setApprovalLevelValidationErrors([]));
  } catch (error: any) {
    console.error('Error creating approval level:', error);
    yield put(setError(error.message || 'Failed to create approval level'));
  } finally {
    yield put(setLoading(false));
  }
}

function* updateApprovalLevelSaga(action: PayloadAction<UpdateApprovalLevelRequest>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    // Validate the approval level
    const validationErrors = yield call(workflowApi.validation.validateApprovalLevel, action.payload);
    if (validationErrors.length > 0) {
      yield put(setApprovalLevelValidationErrors(validationErrors));
      return;
    }
    
    const updatedApprovalLevel = yield call(workflowApi.updateApprovalLevel, action.payload);
    yield put(updateApprovalLevel(updatedApprovalLevel));
    yield put(setApprovalLevelDialogOpen(false));
    yield put(setApprovalLevelValidationErrors([]));
  } catch (error: any) {
    console.error('Error updating approval level:', error);
    yield put(setError(error.message || 'Failed to update approval level'));
  } finally {
    yield put(setLoading(false));
  }
}

function* deleteApprovalLevelSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    yield call(workflowApi.deleteApprovalLevel, action.payload);
    yield put(removeApprovalLevel(action.payload));
  } catch (error: any) {
    console.error('Error deleting approval level:', error);
    yield put(setError(error.message || 'Failed to delete approval level'));
  } finally {
    yield put(setLoading(false));
  }
}

function* bulkActivateWorkflowsSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkLoading(true));
    yield put(setError(null));
    
    yield call(workflowApi.bulkActivate, action.payload);
    yield put(deselectAllWorkflows());
    
    // Refresh the workflows list
    yield call(fetchWorkflowsSaga);
    yield call(fetchWorkflowStatisticsSaga);
  } catch (error: any) {
    console.error('Error bulk activating workflows:', error);
    yield put(setError(error.message || 'Failed to activate workflows'));
  } finally {
    yield put(setBulkLoading(false));
  }
}

function* bulkDeactivateWorkflowsSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkLoading(true));
    yield put(setError(null));
    
    yield call(workflowApi.bulkDeactivate, action.payload);
    yield put(deselectAllWorkflows());
    
    // Refresh the workflows list
    yield call(fetchWorkflowsSaga);
    yield call(fetchWorkflowStatisticsSaga);
  } catch (error: any) {
    console.error('Error bulk deactivating workflows:', error);
    yield put(setError(error.message || 'Failed to deactivate workflows'));
  } finally {
    yield put(setBulkLoading(false));
  }
}

function* fetchWorkflowStatisticsSaga(): Generator<any, void, any> {
  try {
    const statistics = yield call(workflowApi.getStatistics);
    yield put(setStatistics(statistics));
  } catch (error: any) {
    console.error('Error fetching workflow statistics:', error);
    // Don't show error for statistics as it's not critical
  }
}

// Helper saga to auto-fetch related data when needed
function* autoFetchRelatedData(): Generator<any, void, any> {
  try {
    const state: RootState = yield select();
    
    const promises = [];
    
    if (state.workflow.roles.length === 0) {
      promises.push(call(fetchRolesSaga));
    }
    
    if (state.workflow.statuses.length === 0) {
      promises.push(call(fetchStatusesSaga));
    }
    
    if (state.workflow.users.length === 0) {
      promises.push(call(fetchUsersSaga));
    }
    
    if (state.workflow.eventTypes.length === 0) {
      promises.push(call(fetchEventTypesSaga));
    }
    
    if (state.workflow.groups.length === 0) {
      promises.push(call(fetchGroupsSaga));
    }
    
    yield all(promises);
  } catch (error) {
    // Ignore errors in auto-fetch
  }
}

// Watcher Sagas
function* watchFetchWorkflows(): Generator<any, void, any> {
  yield takeLatest(FETCH_WORKFLOWS, fetchWorkflowsSaga);
}

function* watchFetchWorkflowById(): Generator<any, void, any> {
  yield takeEvery(FETCH_WORKFLOW_BY_ID, fetchWorkflowByIdSaga);
}

function* watchCreateWorkflow(): Generator<any, void, any> {
  yield takeEvery(CREATE_WORKFLOW, function*(action: PayloadAction<CreateWorkflowRequest>): Generator<any, void, any> {
    yield call(autoFetchRelatedData);
    yield call(createWorkflowSaga, action);
  });
}

function* watchUpdateWorkflow(): Generator<any, void, any> {
  yield takeEvery(UPDATE_WORKFLOW, function*(action: PayloadAction<UpdateWorkflowRequest>): Generator<any, void, any> {
    yield call(autoFetchRelatedData);
    yield call(updateWorkflowSaga, action);
  });
}

function* watchDeleteWorkflow(): Generator<any, void, any> {
  yield takeEvery(DELETE_WORKFLOW, deleteWorkflowSaga);
}

function* watchFetchRoles(): Generator<any, void, any> {
  yield takeLatest(FETCH_ROLES, fetchRolesSaga);
}

function* watchFetchUsers(): Generator<any, void, any> {
  yield takeLatest(FETCH_USERS, fetchUsersSaga);
}

function* watchFetchStatuses(): Generator<any, void, any> {
  yield takeLatest(FETCH_STATUSES, fetchStatusesSaga);
}

function* watchFetchEventTypes(): Generator<any, void, any> {
  yield takeLatest(FETCH_EVENT_TYPES, fetchEventTypesSaga);
}

function* watchFetchGroups(): Generator<any, void, any> {
  yield takeLatest(FETCH_GROUPS, fetchGroupsSaga);
}

function* watchCreateApprovalLevel(): Generator<any, void, any> {
  yield takeEvery(CREATE_APPROVAL_LEVEL, createApprovalLevelSaga);
}

function* watchUpdateApprovalLevel(): Generator<any, void, any> {
  yield takeEvery(UPDATE_APPROVAL_LEVEL, updateApprovalLevelSaga);
}

function* watchDeleteApprovalLevel(): Generator<any, void, any> {
  yield takeEvery(DELETE_APPROVAL_LEVEL, deleteApprovalLevelSaga);
}

function* watchBulkActivateWorkflows(): Generator<any, void, any> {
  yield takeEvery(BULK_ACTIVATE_WORKFLOWS, bulkActivateWorkflowsSaga);
}

function* watchBulkDeactivateWorkflows(): Generator<any, void, any> {
  yield takeEvery(BULK_DEACTIVATE_WORKFLOWS, bulkDeactivateWorkflowsSaga);
}

function* watchFetchWorkflowStatistics(): Generator<any, void, any> {
  yield takeLatest(FETCH_WORKFLOW_STATISTICS, fetchWorkflowStatisticsSaga);
}

// Root saga
export function* workflowSaga(): Generator<any, void, any> {
  yield all([
    watchFetchWorkflows(),
    watchFetchWorkflowById(),
    watchCreateWorkflow(),
    watchUpdateWorkflow(),
    watchDeleteWorkflow(),
    watchFetchRoles(),
    watchFetchUsers(),
    watchFetchStatuses(),
    watchFetchEventTypes(),
    watchFetchGroups(),
    watchCreateApprovalLevel(),
    watchUpdateApprovalLevel(),
    watchDeleteApprovalLevel(),
    watchBulkActivateWorkflows(),
    watchBulkDeactivateWorkflows(),
    watchFetchWorkflowStatistics(),
  ]);
} 